import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Swal from 'sweetalert2';
import useWindowWidth from '../hooks/useWindowWidth';
import { swalSuccess, swalError } from '../utils/alert';
import { maskCurrency, parseCurrency, formatInitialAmount } from '../utils/currency';
import './ExpenseForm.css';
import './RevenueDesktop.css';

function TransferForm({ transfer, onSuccess }) {
  const [formData, setFormData] = useState(() =>
    transfer
      ? { ...transfer, amount: formatInitialAmount(transfer.amount) }
      : {
          from_bank_id: '',
          to_bank_id: '',
          category_id: '',
          amount: '',
          transfer_date: '',
          description: '',
        }
  );
  const [banks, setBanks] = useState([]);
  const [categories, setCategories] = useState([]);
  const width = useWindowWidth();
  const isMobile = width < 768;

  useEffect(() => {
    api.get('/banks').then(res => setBanks(res.data));
    api.get('/categories').then(res => setCategories(res.data));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'amount') {
      setFormData({ ...formData, amount: maskCurrency(value) });
      return;
    }
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        from_bank_id: formData.from_bank_id,
        to_bank_id: formData.to_bank_id,
        category_id: formData.category_id,
        amount: parseCurrency(formData.amount),
        transfer_date: formData.transfer_date,
        description: formData.description,
      };

      const response = await api.post('/transfers', payload);

      if (response.data.used_overdraft) {
        const formatted = parseFloat(response.data.used_overdraft)
          .toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
        await Swal.fire({
          title: 'Transferência realizada!',
          html: `Transferência concluída com sucesso.<br><br>` +
            `<strong>Atenção:</strong> Foi utilizado <strong>${formatted}</strong> do cheque especial.`,
          icon: 'warning',
          confirmButtonText: 'Ok',
        });
      } else {
        swalSuccess('Transferência realizada com sucesso!');
      }
      onSuccess();
    } catch (error) {
      console.error('Houve um erro ao realizar a transferência!', error);
      swalError(error.response?.data?.message || 'Erro ao realizar transferência.');
    }
  };

  // ════════════════════════════════════
  // MOBILE FORM
  // ════════════════════════════════════
  const renderMobileForm = () => (
    <form onSubmit={handleSubmit} className="expense-form-mobile" autoComplete="off">

      {/* Valor */}
      <div className="form-section amount-section">
        <label className="section-label">Valor</label>
        <div className="amount-input-wrapper">
          <span className="currency-symbol">R$</span>
          <input
            type="text"
            inputMode="numeric"
            className="amount-input"
            name="amount"
            value={formData.amount}
            onChange={handleChange}
            placeholder="0,00"
            autoComplete="off"
            required
          />
        </div>
      </div>

      {/* Data */}
      <div className="form-section">
        <label className="section-label">Data da Transferência</label>
        <input
          type="date"
          className="form-control"
          name="transfer_date"
          value={formData.transfer_date}
          onChange={handleChange}
          required
        />
      </div>

      {/* Conta Origem */}
      <div className="form-section">
        <label className="section-label">Conta de Origem</label>
        <select
          className="form-select form-select-lg"
          name="from_bank_id"
          value={formData.from_bank_id}
          onChange={handleChange}
          required
        >
          <option value="">Selecionar conta</option>
          {banks.map(bank => (
            <option key={bank.id} value={bank.id}>{bank.name} — {bank.account_number}</option>
          ))}
        </select>
      </div>

      {/* Conta Destino */}
      <div className="form-section">
        <label className="section-label">Conta de Destino</label>
        <select
          className="form-select form-select-lg"
          name="to_bank_id"
          value={formData.to_bank_id}
          onChange={handleChange}
          required
        >
          <option value="">Selecionar conta</option>
          {banks
            .filter(bank => String(bank.id) !== String(formData.from_bank_id))
            .map(bank => (
              <option key={bank.id} value={bank.id}>{bank.name} — {bank.account_number}</option>
            ))}
        </select>
      </div>

      {/* Categoria */}
      <div className="form-section">
        <label className="section-label">Categoria</label>
        <select
          className="form-select form-select-lg"
          name="category_id"
          value={formData.category_id}
          onChange={handleChange}
          required
        >
          <option value="">Selecionar categoria</option>
          {categories.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </select>
      </div>

      {/* Descrição */}
      <div className="form-section">
        <label className="section-label">Descrição (Opcional)</label>
        <textarea
          className="form-control"
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows="3"
          placeholder="Observações sobre a transferência..."
        />
      </div>

      {/* Botão fixo */}
      <div className="mobile-form-footer">
        <button type="submit" className="btn btn-primary btn-lg w-100">
          Realizar Transferência
        </button>
      </div>
    </form>
  );

  // ════════════════════════════════════
  // DESKTOP FORM
  // ════════════════════════════════════
  const renderDesktopForm = () => (
    <form onSubmit={handleSubmit} className="rv-form-desktop">

      {/* Contas Origem e Destino */}
      <div className="rv-form-grid-2">
        <div className="rv-form-field">
          <label className="rv-form-label" htmlFor="from_bank_id">Conta de Origem</label>
          <select
            className="rv-form-select"
            id="from_bank_id"
            name="from_bank_id"
            value={formData.from_bank_id}
            onChange={handleChange}
            required
          >
            <option value="">Selecionar conta</option>
            {banks.map(bank => (
              <option key={bank.id} value={bank.id}>{bank.name} — {bank.account_number}</option>
            ))}
          </select>
        </div>
        <div className="rv-form-field">
          <label className="rv-form-label" htmlFor="to_bank_id">Conta de Destino</label>
          <select
            className="rv-form-select"
            id="to_bank_id"
            name="to_bank_id"
            value={formData.to_bank_id}
            onChange={handleChange}
            required
          >
            <option value="">Selecionar conta</option>
            {banks
              .filter(bank => String(bank.id) !== String(formData.from_bank_id))
              .map(bank => (
                <option key={bank.id} value={bank.id}>{bank.name} — {bank.account_number}</option>
              ))}
          </select>
        </div>
      </div>

      {/* Valor + Data */}
      <div className="rv-form-grid-2">
        <div className="rv-form-field">
          <label className="rv-form-label" htmlFor="amount">Valor (R$)</label>
          <input
            type="text"
            inputMode="numeric"
            className="rv-form-input"
            id="amount"
            name="amount"
            value={formData.amount}
            onChange={handleChange}
            placeholder="0,00"
            required
          />
        </div>
        <div className="rv-form-field">
          <label className="rv-form-label" htmlFor="transfer_date">Data da Transferência</label>
          <input
            type="date"
            className="rv-form-input"
            id="transfer_date"
            name="transfer_date"
            value={formData.transfer_date}
            onChange={handleChange}
            required
          />
        </div>
      </div>

      {/* Categoria */}
      <div className="rv-form-field-full">
        <label className="rv-form-label" htmlFor="category_id">Categoria</label>
        <select
          className="rv-form-select"
          id="category_id"
          name="category_id"
          value={formData.category_id}
          onChange={handleChange}
          required
        >
          <option value="">Selecionar categoria</option>
          {categories.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </select>
      </div>

      {/* Descrição */}
      <div className="rv-form-field-full">
        <label className="rv-form-label" htmlFor="description">Descrição (Opcional)</label>
        <textarea
          className="rv-form-textarea"
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Observações sobre a transferência..."
        />
      </div>

      {/* Salvar */}
      <div className="rv-form-footer">
        <button type="submit" className="rv-btn-submit">Realizar Transferência</button>
      </div>
    </form>
  );

  return isMobile ? renderMobileForm() : renderDesktopForm();
}

export default TransferForm;
